import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import Icon from "../../components/Icon";
import ConfirmDialog from "../../components/ConfirmDialog";
import {
  RESPONSE_PAGE_SIZE,
  SURVEY_ACTIONS,
  defaultRange,
  formatDateTime,
  formatScore,
  useSurveyRequest
} from "./api";
import { AUTO_REFRESH_MS } from "../NotificationList/api";
import { choiceBars, histogramBars, scaleBars } from "./summary";
import "./style.scss";

const DEFAULT_MONTHS_BACK = 3;

// The page shows the CSAT surveys only; NPS has been taken off this screen.
const isCsatSurvey = survey => /^CSAT/i.test((survey && survey.code) || "");

// A five-point star, filled up to the score, for a star-scale survey (CSAT).
const STAR_PATH =
  "M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z";

const Stars = ({ value, max }) => (
  <span className="survey-stars">
    {Array.from({ length: max }, (_, index) => (
      <svg
        key={index}
        className={index < value ? "survey-star on" : "survey-star"}
        width="15"
        height="15"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d={STAR_PATH} />
      </svg>
    ))}
  </span>
);

/*
 * "Use CSAT to monitor the application experience".
 *
 * The page answers two questions and keeps them apart on purpose. The cards and the histogram
 * say what the score is. The table underneath says why: a CSAT of 3.4 is not something anyone
 * can act on, but the six sentences customers typed next to it are.
 *
 * Nothing here can change a survey. The questions live in be-notification's config so that the
 * app renders whatever it is handed, and a question edited from this screen would silently
 * change the meaning of every score already recorded against it.
 */
function SurveyInsight() {
  const requestSurvey = useSurveyRequest();

  const [surveys, SETxSurveys] = useState([]);
  const [activeCode, SETxActiveCode] = useState("");
  const [range, SETxRange] = useState(() => defaultRange(DEFAULT_MONTHS_BACK));
  const [appliedRange, SETxAppliedRange] = useState(() => defaultRange(DEFAULT_MONTHS_BACK));
  const [summary, SETxSummary] = useState(null);
  const [responses, SETxResponses] = useState([]);
  const [pagination, SETxPagination] = useState(null);
  const [page, SETxPage] = useState(1);
  const [loading, SETxLoading] = useState(false);
  const [error, SETxError] = useState(null);

  // A reply that arrives after the operator has moved on is dropped rather than written over
  // newer state - switching tabs quickly used to leave one survey's score above another's rows.
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const describeError = requestError =>
    requestError && requestError.status === 404
      ? "Survey not found in be-notification"
      : (requestError && requestError.message) || "Failed to load data";

  /*
   * The switch for the automatic CSAT invitation (after an account approval, an MF order, a PVD
   * transaction or app sign-up). null until it is read; it reads as on when nothing was stored.
   */
  const [csatSetting, SETxCsatSetting] = useState(null);
  const [csatSaving, SETxCsatSaving] = useState(false);
  const [csatError, SETxCsatError] = useState(null);
  const [shwCsatOffConfirm, SETxShwCsatOffConfirm] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const result = await requestSurvey(SURVEY_ACTIONS.setting);
        if (live && !result.signedOut) {
          SETxCsatSetting(result.data);
        }
      } catch (requestError) {
        if (live) {
          SETxCsatError(`Could not read the automatic CSAT switch: ${describeError(requestError)}`);
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [requestSurvey]);

  const SAVExCsatSetting = async enabled => {
    SETxShwCsatOffConfirm(false);
    SETxCsatSaving(true);
    SETxCsatError(null);
    try {
      const result = await requestSurvey(SURVEY_ACTIONS.updateSetting, { payload: { enabled } });
      if (mountedRef.current && !result.signedOut) {
        SETxCsatSetting(result.data);
      }
    } catch (requestError) {
      if (mountedRef.current) {
        SETxCsatError(`Could not change the automatic CSAT switch: ${describeError(requestError)}`);
      }
    } finally {
      if (mountedRef.current) {
        SETxCsatSaving(false);
      }
    }
  };

  // Turning it on takes effect straight away; turning it off stops every invitation, so it asks first.
  const TOGGLExCsat = () => {
    if (csatSetting && csatSetting.enabled) {
      SETxShwCsatOffConfirm(true);
    } else {
      SAVExCsatSetting(true);
    }
  };

  const csatOn = Boolean(csatSetting && csatSetting.enabled);

  /* Which surveys exist. Served, not hard-coded, so a new survey needs no dashboard release. */
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const result = await requestSurvey(SURVEY_ACTIONS.list);
        if (!live || result.signedOut) {
          return;
        }
        const list = ((result.data && result.data.data) || []).filter(isCsatSurvey);
        SETxSurveys(list);
        SETxActiveCode(current => current || (list[0] && list[0].code) || "");
      } catch (requestError) {
        if (live) {
          SETxError(describeError(requestError));
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [requestSurvey]);

  /*
   * `silent` is the background refresh: no loading overlay, and a failure leaves the figures
   * already on screen with no error, since the next refresh tries again.
   */
  const fetchSurvey = useCallback(
    async (code, activeRange, activePage, silent) => {
      if (!code) {
        return;
      }
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      const isCurrent = () => mountedRef.current && requestIdRef.current === requestId;

      if (!silent) {
        SETxLoading(true);
        SETxError(null);
      }
      try {
        const params = { from: activeRange.from, to: activeRange.to };
        const [summaryResult, responseResult] = await Promise.all([
          requestSurvey(SURVEY_ACTIONS.summary, { code, query: params }),
          requestSurvey(SURVEY_ACTIONS.responses, {
            code,
            query: { ...params, page: activePage, limit: RESPONSE_PAGE_SIZE }
          })
        ]);

        if (!isCurrent() || summaryResult.signedOut || responseResult.signedOut) {
          return;
        }
        SETxSummary(summaryResult.data);
        SETxResponses((responseResult.data && responseResult.data.data) || []);
        SETxPagination((responseResult.data && responseResult.data.pagination) || null);
        SETxError(null);
      } catch (requestError) {
        if (!isCurrent() || silent) {
          return;
        }
        SETxSummary(null);
        SETxResponses([]);
        SETxPagination(null);
        SETxError(describeError(requestError));
      } finally {
        if (isCurrent() && !silent) {
          SETxLoading(false);
        }
      }
    },
    [requestSurvey]
  );

  useEffect(() => {
    fetchSurvey(activeCode, appliedRange, page);
  }, [fetchSurvey, activeCode, appliedRange, page]);

  // Customers answer from the app while this page is open, so it re-reads itself every
  // AUTO_REFRESH_MS. Paused while a load is running and while the browser tab is hidden.
  useEffect(() => {
    if (loading) {
      return undefined;
    }
    const timerId = setInterval(() => {
      if (!document.hidden) {
        fetchSurvey(activeCode, appliedRange, page, true);
      }
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timerId);
  }, [fetchSurvey, activeCode, appliedRange, page, loading]);

  const activeSurvey = useMemo(
    () => surveys.find(survey => survey.code === activeCode) || null,
    [surveys, activeCode]
  );

  const bars = useMemo(() => scaleBars(activeSurvey, summary), [activeSurvey, summary]);

  // The scored question's scale decides how a response renders: a 0..1 question is Yes / No,
  // anything wider is drawn as stars filled up to the score.
  const scoreScale = useMemo(() => {
    if (!activeSurvey) {
      return null;
    }
    const scored = (activeSurvey.questions || []).find(
      question => question.id === activeSurvey.scoreQuestionId
    );
    return (scored && scored.scale) || null;
  }, [activeSurvey]);
  const isBinary = Boolean(scoreScale && scoreScale.min === 0 && scoreScale.max === 1);

  // Green for a good score, amber in the middle, red for a poor one — judged against the scale.
  const scoreTone = score => {
    if (isBinary) {
      return score >= 1 ? "good" : "bad";
    }
    const max = (scoreScale && scoreScale.max) || 5;
    const ratio = max > 0 ? score / max : 0;
    if (ratio >= 0.8) {
      return "good";
    }
    return ratio >= 0.5 ? "mid" : "bad";
  };

  // The per-question breakdowns behind the headline score: the CES scale and the
  // "what to improve" choice distribution (served since requirement sheet 5B).
  const questionSummaries = useMemo(() => (summary && summary.questionSummaries) || [], [summary]);

  const totalPages = (pagination && pagination.totalPages) || 0;

  const onApply = event => {
    event.preventDefault();
    SETxPage(1);
    SETxAppliedRange({ ...range });
  };

  const onReset = () => {
    const cleared = { from: "", to: "" };
    SETxRange(cleared);
    SETxPage(1);
    SETxAppliedRange(cleared);
  };

  return (
    <Fragment>
      {loading ? (
        <div className="survey-progress">
          <CircularProgress />
          <p className="survey-progress-text">Loading survey results...</p>
        </div>
      ) : null}

      <div className="survey-container">
        {/* On the breadcrumb line, where Notification List keeps Create new. */}
        <div className="survey-topbar">
          {csatSetting && csatSetting.updatedBy ? (
            <span className="survey-switch-changed">
              Changed by {csatSetting.updatedBy} · {formatDateTime(csatSetting.updatedAt)}
            </span>
          ) : null}
          <span className="survey-switch-label" id="survey-csat-switch-label">
            Automatic CSAT
          </span>
          <div className={csatOn ? "survey-switch-track survey-switch-track--active" : "survey-switch-track"}>
            <button
              type="button"
              role="switch"
              aria-checked={csatOn}
              aria-labelledby="survey-csat-switch-label"
              className={csatOn ? "survey-switch-thumb survey-switch-thumb--active" : "survey-switch-thumb"}
              disabled={!csatSetting || csatSaving}
              onClick={TOGGLExCsat}
            >
              {csatOn ? "Active" : "Inactive"}
            </button>
          </div>
        </div>

        {csatError ? <div className="survey-error survey-switch-error">{csatError}</div> : null}

        <form className="survey-filter" onSubmit={onApply}>
          <div className="survey-filter-field">
            <span className="survey-filter-label">From date</span>
            <input
              type="date"
              value={range.from}
              onChange={event => SETxRange({ ...range, from: event.target.value })}
            />
          </div>
          <div className="survey-filter-field">
            <span className="survey-filter-label">To date</span>
            <input
              type="date"
              value={range.to}
              onChange={event => SETxRange({ ...range, to: event.target.value })}
            />
          </div>
          <div className="survey-filter-actions">
            <button type="submit" className="survey-apply">
              <Icon name="filter" size={18} />
              View
            </button>
            <button type="button" className="survey-reset" onClick={onReset}>
              <Icon name="refresh" size={18} />
              Clear dates
            </button>
          </div>
        </form>

        {error ? <div className="survey-error">{error}</div> : null}

        <div className="survey-cards">
          <div className="survey-card">
            <span className="survey-card-label">Responses</span>
            <span className="survey-card-value">{(summary && summary.total) || 0}</span>
          </div>
          <div className="survey-card">
            <span className="survey-card-label">Average score</span>
            <span className="survey-card-value">
              {formatScore(summary && summary.averageScore, summary && summary.total)}
            </span>
          </div>
        </div>

        {bars.length > 0 ? (
          <div className="survey-histogram">
            <p className="survey-section-title">Score distribution</p>
            {bars.map(bar => (
              <div className="survey-bar-row" key={bar.score}>
                <span className="survey-bar-score">{bar.score}</span>
                <span className="survey-bar-track">
                  <span className="survey-bar-fill" style={{ width: `${bar.percent}%` }} />
                </span>
                <span className="survey-bar-count">{bar.count}</span>
              </div>
            ))}
          </div>
        ) : null}

        {questionSummaries.map(question => (
          <div className="survey-breakdown" key={question.id}>
            <p className="survey-section-title">{question.titleEn || question.titleTh}</p>
            {question.type === "SCALE" ? (
              <Fragment>
                <p className="survey-breakdown-meta">
                  Average {formatScore(question.averageScore, question.answered)} · {question.answered} answers
                </p>
                <div className="survey-histogram">
                  {histogramBars(
                    question.histogram,
                    question.scale && question.scale.min,
                    question.scale && question.scale.max
                  ).map(bar => (
                    <div className="survey-bar-row" key={bar.score}>
                      <span className="survey-bar-score">{bar.score}</span>
                      <span className="survey-bar-track">
                        <span className="survey-bar-fill" style={{ width: `${bar.percent}%` }} />
                      </span>
                      <span className="survey-bar-count">{bar.count}</span>
                    </div>
                  ))}
                </div>
              </Fragment>
            ) : (
              <div className="survey-histogram">
                {choiceBars(question.choices).map(bar => (
                  <div className="survey-bar-row" key={bar.value}>
                    <span className="survey-bar-label">{bar.label}</span>
                    <span className="survey-bar-track">
                      <span className="survey-bar-fill" style={{ width: `${bar.percent}%` }} />
                    </span>
                    <span className="survey-bar-count">{bar.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <p className="survey-section-title">Individual responses</p>

        <div className="survey-table-wrap">
          <table className="survey-table">
            <thead>
              <tr>
                <th className="survey-col-submitted">Submitted</th>
                <th className="survey-col-score">Score</th>
                <th className="survey-col-uuid">Customer uuid</th>
                <th className="survey-col-group">Customer group · app</th>
              </tr>
            </thead>
            <tbody>
              {responses.map(response => (
                <tr key={response.id}>
                  <td>{formatDateTime(response.createdAt)}</td>
                  <td>
                    <div className="survey-score-cell">
                      <span className={`survey-score-badge ${scoreTone(response.score)}`}>
                        {response.score}
                      </span>
                      {isBinary ? (
                        <span className="survey-yesno">{response.score >= 1 ? "Yes" : "No"}</span>
                      ) : scoreScale ? (
                        <Stars value={response.score} max={scoreScale.max} />
                      ) : null}
                    </div>
                  </td>
                  <td className="survey-cell-uuid">{response.customerUuid || "-"}</td>
                  <td>
                    {response.channel || "-"}
                    {response.appVersion ? ` · v${response.appVersion}` : ""}
                  </td>
                </tr>
              ))}

              {!loading && responses.length === 0 ? (
                <tr>
                  <td className="survey-empty" colSpan={4}>
                    No responses in this date range
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 ? (
          <div className="survey-pager">
            <span className="survey-pager-count">
              {(pagination.page - 1) * (pagination.limit || RESPONSE_PAGE_SIZE) + 1}–
              {(pagination.page - 1) * (pagination.limit || RESPONSE_PAGE_SIZE) + responses.length} of{" "}
              {pagination.total}
            </span>
            <div className="survey-pager-nav">
              <button type="button" disabled={page <= 1} onClick={() => SETxPage(page - 1)}>
                ‹ Prev
              </button>
              <span className="survey-pager-page">
                {pagination.page} / {totalPages || 1}
              </span>
              <button
                type="button"
                disabled={totalPages ? page >= totalPages : true}
                onClick={() => SETxPage(page + 1)}
              >
                Next ›
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {shwCsatOffConfirm ? (
        <ConfirmDialog
          title="Turn off the automatic CSAT invitation?"
          labelId="survey-csat-off-title"
          onDismiss={() => SETxShwCsatOffConfirm(false)}
          actions={[
            { label: "Cancel", onClick: () => SETxShwCsatOffConfirm(false), autoFocus: true },
            { label: "Turn off", onClick: () => SAVExCsatSetting(false), primary: true }
          ]}
        >
          Customers stop getting the CSAT invitation after an account approval, an MF order, a PVD
          transaction or app sign-up until it is turned back on. Invitations already in their inbox
          can still be answered.
        </ConfirmDialog>
      ) : null}
    </Fragment>
  );
}

export default SurveyInsight;
